import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { Action, ActionResize } from 'expo-image-manipulator'
import { ImagePickerResult } from 'expo-image-picker'
import { useGlobalSearchParams, useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import React, { useCallback, useEffect } from 'react'
import { Dimensions, FlatList, Platform, TouchableOpacity, View } from 'react-native'
import { ms, mvs, ScaledSheet } from 'react-native-size-matters'
import { useDispatch, useSelector } from 'react-redux'
import ModalScreen, { getModalStyleDefinition } from '~components/ModalScreen'
import { Text } from '~components/Themed'
import { isSmallDevice } from '~constants/Layout'
import {
  IMAGE_MAX_DIMENSION_ONE_SECTION,
  IMAGE_MAX_DIMENSION_OVER_ONE_SECTION,
} from '~constants/Settings'
import { ApplicationState } from '~redux'
import { setChatImage } from '~redux/chat/actions'
import { useTheme } from '~theme/ThemeManager'
import { MediaSourceType } from '~types'
import useChatRouteParams from '~hooks/useChatRouteParams'

import * as ImageManipulator from 'expo-image-manipulator'
import * as ImagePicker from 'expo-image-picker'

type MediaSource = {
  key: MediaSourceType
  name: string
  icon: IconProp
  action: () => void
}

export default function MediaSourceScreen(): React.JSX.Element {
  const router = useRouter()
  const { target } = useLocalSearchParams()
  const navigation = useNavigation()
  const { theme } = useTheme()
  const styles = useStyles()
  const { chat } = useSelector((state: ApplicationState) => state.chat)
  const dispatch = useDispatch()

  const { folderId, chatId, userId } = useGlobalSearchParams<{
    folderId: string
    chatId: string
    userId?: string // Editting a reply
  }>()

  const chatRouteParams = useChatRouteParams()

  const processImage = useCallback(
    async (index: number, pickerResult: ImagePickerResult) => {
      if (pickerResult.canceled) {
        return
      }

      const result = pickerResult.assets[0]

      const {
        layout: { rows, columns },
      } = chat
      const maxDimension =
        columns === 1 && rows === 1
          ? IMAGE_MAX_DIMENSION_ONE_SECTION
          : IMAGE_MAX_DIMENSION_OVER_ONE_SECTION

      // Maintain aspect ratio but limit to a maximum size depending on the amount of sections in the chat
      const resize: ActionResize | null =
        result.width > maxDimension || result.height > maxDimension
          ? {
              resize:
                result.width > result.height
                  ? {
                      width: maxDimension,
                    }
                  : {
                      height: maxDimension,
                    },
            }
          : null

      const actions: Action[] = []
      if (resize != null) {
        actions.push(resize)
      }

      const manipulated = await ImageManipulator.manipulateAsync(
        result.uri,
        actions,
        // Compress as a JPEG
        { compress: 0.6, format: ImageManipulator.SaveFormat.JPEG },
      )

      // Persist it
      dispatch(
        setChatImage({
          chat: { id: chat.id, path: chat.path },
          index,
          uri: manipulated.uri,
        }),
      )
    },
    [chat, dispatch],
  )

  const sources: MediaSource[] = React.useMemo(() => {
    async function openCameraAsync() {
      if (typeof target !== 'string') {
        return
      }

      const permissionResult = await ImagePicker.requestCameraPermissionsAsync()

      if (permissionResult.granted === false) {
        alert('Permission to access camera is required!')
        return
      }

      const pickerResult = await ImagePicker.launchCameraAsync({
        quality: 0.5,
        exif: false,
      })

      router.dismissTo(chatRouteParams)

      await processImage(Number.parseInt(target), pickerResult)
    }

    async function openImagePickerAsync() {
      if (typeof target !== 'string') {
        return
      }

      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync()

      if (permissionResult.granted === false) {
        alert('Permission to access camera roll is required!')
        return
      }

      const pickerResult = await ImagePicker.launchImageLibraryAsync()

      router.dismissTo(chatRouteParams)

      await processImage(Number.parseInt(target), pickerResult)
    }

    const sources: MediaSource[] = []

    if (Platform.OS !== 'web') {
      sources.push({
        key: 'camera',
        name: 'Camera',
        icon: ['fal', 'camera'],
        action: () => openCameraAsync(),
      })
    }

    sources.push(
      {
        key: 'device',
        name: 'Device',
        icon: ['fal', 'photo-video'],
        action: () => openImagePickerAsync(),
      },
      {
        key: 'community',
        name: Dimensions.get('window').width > 640 ? 'Chatta\nCommunity' : 'Chatta Community',
        icon: ['fal', 'hands-heart'],
        action: () => {
          router.navigate({
            pathname: `/folders/${folderId}/chats/${chatId}/media-source/community`,
            params: {
              type: 'community',
              target,
              userId,
            },
          })
        },
      },
      {
        key: 'internet',
        name: Dimensions.get('window').width > 640 ? 'Search the\ninternet' : 'Search the internet',
        icon: ['fal', 'globe'],
        action: () => {
          router.navigate({
            pathname: `/folders/${folderId}/chats/${chatId}/media-source/internet`,
            params: {
              type: 'internet',
              target,
              userId,
            },
          })
        },
      },
    )

    return sources
  }, [chatId, chatRouteParams, folderId, processImage, router, target, userId])

  useEffect(() => {
    if (!chat.path) {
      router.dismissTo(chatRouteParams)
    }
  }, [chat.path, chatRouteParams, router])

  return (
    <ModalScreen
      ContainerComponent={React.Fragment}
      height={Dimensions.get('window').width > 600 ? mvs(400, 0.8) : undefined}
    >
      <FlatList
        contentContainerStyle={[styles.content]}
        style={styles.list}
        scrollEnabled={false}
        numColumns={isSmallDevice ? 1 : sources.length}
        columnWrapperStyle={isSmallDevice ? undefined : styles.column}
        data={sources}
        ItemSeparatorComponent={() => <View style={styles.listVerticalSeparator} />}
        ListHeaderComponent={() => (
          <View style={styles.titleContainer}>
            <Text style={styles.title}>Media Source</Text>
            <Text style={styles.subtitle}>
              Take a photo, add images from your library or the search the Chatta Community or
              internet
            </Text>
          </View>
        )}
        renderItem={({ item }) => (
          <TouchableOpacity
            style={styles.sourceContainer}
            onPress={() => {
              if (navigation.canGoBack()) {
                item.action()
              }
            }}
          >
            <FontAwesomeIcon
              color={theme.primary}
              style={styles.sourceIcon}
              size={ms(24, 0.9)}
              icon={item.icon}
            />
            <Text style={[styles.sourceText]}>{item.name}</Text>
          </TouchableOpacity>
        )}
      />
    </ModalScreen>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    list: {
      alignSelf: 'center',
    },
    listVerticalSeparator: {
      height: Dimensions.get('window').width > 640 ? '20@mvs' : '10@mvs',
    },
    sourceContainer: {
      backgroundColor: theme.white,
      borderWidth: 1,
      borderColor: theme[300],
      borderRadius: '5@ms',
      marginHorizontal: '10@ms',
      width: Dimensions.get('window').width > 640 ? '90@ms' : undefined,
      flexDirection: Dimensions.get('window').width > 640 ? 'column' : 'row',
      padding: Dimensions.get('window').width > 640 ? '15@ms' : '10@ms',
      alignItems: 'center',
    },
    sourceIcon: {
      marginTop: Dimensions.get('window').width > 640 ? '10@ms' : undefined,
      marginBottom: Dimensions.get('window').width > 640 ? '20@ms' : undefined,
      marginRight: Dimensions.get('window').width > 640 ? undefined : '10@ms',
    },
    sourceText: {
      fontSize: '10@ms',
      textAlign: 'center',
      fontWeight: '700',
      color: theme[800],
    },
    column: {
      justifyContent: 'center',
    },
  })
}
